/**
 * RecipeCreationModal — unified recipe-creation surface.
 *
 * Three modes:
 *   - `new`       — create a fresh recipe (name + optional collection picker).
 *   - `spinoff`   — spin off an existing recipe to a sibling (name + collection
 *                   picker pre-populated from the source recipe).
 *   - `variation` — create a new variation under a parent recipe (name only;
 *                   collections are inherited from the parent).
 *
 * The non-`new` modes share the same chrome and validation. Each mode has its
 * own header copy, submit-button label, and onCreate input shape.
 *
 * Followup Bug B (2026-05-02) seeded the `new` mode. The 2026-05-03 follow-on
 * Opus pass wires `spinoff` and `variation`, retiring SpinOffModal and
 * CreateVariationModal in the same diff.
 *
 * Design refs:
 *   design.md §10.6 — recipe-overflow kebab order; new-recipe entry point
 *   design.md §10   — Variations sub-mode; spin-off + variation modal flow
 *   branding.md §5  — voice (sentence-case, no exclamations)
 *   branding.md §8  — vocabulary: "Recipe" not "Repository"; "Spin-off"
 *                     not "Fork"; "Variation" not "branch"
 */

import {
  useState,
  useEffect,
  useRef,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import type { Collection } from "./types";
import { useErrorReporter } from "~/hooks/useErrorReporter";
import { withTimeout, TimeoutError } from "~/lib/withTimeout";
import { PasteConvertPane } from "./PasteConvertPane";
import type { PasteConvertFn, PasteClaims, PasteClaimsProbe } from "./pasteConvert";
import { DiffSegmented } from "../diff/DiffSegmented";

// Rule 8: a create call that never settles must not latch "Saving…" forever.
// After this deadline the handler surfaces a failure and re-enables retry.
const CREATE_TIMEOUT_MS = 15_000;

// ─── Modes ────────────────────────────────────────────────────────────────────

export type RecipeCreationMode = "new" | "spinoff" | "variation";

// ─── Props ────────────────────────────────────────────────────────────────────

/**
 * onCreate input shape, by mode:
 *   `new`       — { title, collectionUuids, mode }
 *   `spinoff`   — { title, collectionUuids, mode } where caller already knows
 *                 sourceRecipe.uuid via the `sourceRecipe` prop.
 *   `variation` — { title, mode } — variations inherit parent's collections, so
 *                 collectionUuids is always [] and the picker is hidden.
 *                 Slug derivation lives in the caller (matches existing branch
 *                 RPC contract: { recipeUuid, slug }).
 */
export interface RecipeCreationModalProps {
  /** Mode driving which sub-surface renders. */
  mode: RecipeCreationMode;
  /** All collections the user owns — drives the `new` and `spinoff` pickers. */
  collections: Collection[];
  /**
   * Resolves with the new recipe (or variation) UUID on success. Throws on
   * failure so the modal keeps the inline error visible (Rule 8).
   *
   * For `variation` mode the returned identifier is the new branch UUID; for
   * `new` and `spinoff` modes it is the new recipe UUID. Callers that don't
   * need the value can ignore it.
   */
  onCreate: (input: {
    title: string;
    collectionUuids: string[];
    mode: RecipeCreationMode;
  }) => Promise<string>;
  onClose: () => void;
  /**
   * Source-recipe context. Required for `spinoff` and `variation` modes;
   * ignored for `new`. Drives the header context line ("Spin-off from: …" /
   * "Variation of: …").
   */
  sourceRecipe?: { uuid: string; title: string };
  /**
   * Pre-selected collection UUIDs for `spinoff` mode — typically the source
   * recipe's collection memberships, which the user can edit before submit.
   * Ignored for `new` and `variation` modes.
   */
  sourceRecipeCollectionUuids?: string[];
  /**
   * Titles of the user's existing recipes. When the typed name matches one
   * case-insensitively, an inline (non-blocking) hint is shown — the user may
   * still create the recipe (duplicate titles are allowed). Typically passed
   * for `new` / `spinoff` modes; `variation` names live in a different
   * namespace, so callers omit it there.
   */
  existingRecipeTitles?: string[];
  /**
   * Copy overrides for the "Duplicate" entry point (nav-chrome-contextual-create
   * §4.3). Duplicating your OWN recipe is mechanically a `spinoff`, but the
   * word "spin off" is reserved for adopting someone ELSE's shared recipe
   * (branding.md §8). These let the RecipeHeader kebab reuse `mode="spinoff"`
   * while presenting "Duplicate recipe" / "Duplicate" copy. When omitted the
   * mode's default copy is used.
   */
  headerLabel?: string;
  submitLabel?: string;
  submittingLabel?: string;
  /** Overrides the "Spin-off from: " source-context prefix (e.g. "Duplicate of: "). */
  sourceContextLabel?: string;
  /**
   * Paste-to-syntax (C-WP5). When provided AND `mode === "new"`, the modal gains
   * a "Paste a recipe" sub-mode: a segmented toggle switches the body between the
   * blank name+collections form and the {@link PasteConvertPane}. Omitting it
   * leaves the modal exactly as before (spin-off / variation / duplicate callers
   * never pass it). The call resolves with the new recipe UUID; on success the
   * caller (via `onPasteConverted`) closes the modal and navigates to the editor.
   */
  pasteConvert?: PasteConvertFn;
  /** Called with the new recipe UUID + typed title after a successful paste-convert. */
  onPasteConverted?: (recipeUuid: string, title: string) => void;
  /**
   * Pre-resolved assistant claims for the paste pane's inline at-cap / opted-out
   * states (tests). When omitted the pane probes them itself.
   */
  pasteClaims?: PasteClaims | null;
  /** Injectable claims probe for the paste pane (tests). */
  probePasteClaims?: PasteClaimsProbe;
}

// ─── CSS ──────────────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "var(--scrim)",
  display: "flex",
  zIndex: 1000,
};

const SHEET_CSS = `
  /*
   * Bug 3c fix: use 767px breakpoint (matching the capsule's mobile threshold)
   * instead of 639px so the full-screen sheet rendering applies consistently
   * with the rest of the mobile chrome.
   */
  @media (max-width: 767px) {
    .proof-recipe-create-overlay {
      align-items: flex-end !important;
      justify-content: stretch !important;
    }
    .proof-recipe-create-panel {
      border-radius: var(--radius-sheet-top) var(--radius-sheet-top) 0 0 !important;
      max-width: 100% !important;
      width: 100% !important;
      /* Full available height on mobile to keep submit button above keyboard */
      max-height: 92dvh !important;
    }
  }
  @media (min-width: 768px) {
    .proof-recipe-create-overlay {
      align-items: center;
      justify-content: center;
    }
    .proof-recipe-create-panel {
      width: 420px;
    }
  }
  .proof-recipe-create-collection-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) var(--space-3);
    cursor: pointer;
    border-radius: var(--radius-button);
    min-height: var(--tap-target-min);
  }
  .proof-recipe-create-collection-row:hover {
    background: color-mix(in srgb, var(--text) 4%, transparent);
  }
`;

const panelStyle: React.CSSProperties = {
  background: "var(--surface)",
  borderRadius: "var(--radius-card)",
  // --line-strong (not --line): the modal panel sits on --bg, a ~0.4% lightness
  // delta from --surface, where the pale --line hairline is unreadable. A
  // stronger warm-gray hairline gives the box a visible edge without a shadow
  // (design.md §2 hairlines-not-shadows, §4.6 no modal shadow).
  border: "1px solid var(--line-strong)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  maxHeight: "calc(100dvh - var(--space-8))",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "var(--space-4) var(--space-4) var(--space-3)",
  borderBottom: "1px solid var(--line)",
  flexShrink: 0,
};

const titleStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "14px",
  fontWeight: 500,
  color: "var(--text)",
};

const closeButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "var(--muted)",
  fontSize: "18px",
  lineHeight: 1,
  padding: "var(--space-1)",
  borderRadius: "4px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "color var(--motion-state)",
};

const bodyStyle: React.CSSProperties = {
  padding: "var(--space-4)",
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-3)",
  overflowY: "auto",
};

const labelStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  color: "var(--muted)",
  display: "block",
};

function inputStyle(hasError: boolean): React.CSSProperties {
  return {
    width: "100%",
    border: hasError ? "1px solid var(--del)" : "1px solid var(--line-strong)",
    borderRadius: "var(--radius-button)",
    background: "transparent",
    padding: "var(--space-2) var(--space-3)",
    fontFamily: "var(--font-sans)",
    fontSize: "13px",
    color: "var(--text)",
    outline: "none",
    minHeight: "var(--tap-target-min)",
    boxSizing: "border-box",
    transition: "border-color var(--motion-state)",
  };
}

const collectionListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-1)",
  maxHeight: "200px",
  overflowY: "auto",
  border: "1px solid var(--line-strong)",
  borderRadius: "var(--radius-button)",
  padding: "var(--space-1)",
};

const checkboxStyle: React.CSSProperties = {
  width: "16px",
  height: "16px",
  flexShrink: 0,
  accentColor: "var(--accent)",
  cursor: "pointer",
};

const collectionLabelStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "13px",
  color: "var(--text)",
  flex: "1 1 0",
};

const emptyCollectionsStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  color: "var(--muted)",
  padding: "var(--space-3)",
  textAlign: "center",
};

const errorStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  color: "var(--del)",
};

// Duplicate-name hint — muted (not error red): this is an informative heads-up,
// not a validation failure. The user can still create the recipe.
const duplicateHintStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  color: "var(--muted)",
  display: "block",
  marginTop: "var(--space-1)",
};

const footerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  gap: "var(--space-2)",
  justifyContent: "flex-end",
  padding: "var(--space-3) var(--space-4)",
  borderTop: "1px solid var(--line)",
  flexShrink: 0,
};

function cancelButtonStyle(): React.CSSProperties {
  return {
    background: "none",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-button)",
    padding: "var(--space-2) var(--space-4)",
    fontFamily: "var(--font-sans)",
    fontSize: "13px",
    color: "var(--muted)",
    cursor: "pointer",
    minHeight: "var(--tap-target-min)",
    transition: "color var(--motion-state), border-color var(--motion-state)",
  };
}

/**
 * Primary CTA. Disabled uses the shared neutral disabled tokens, not a washed
 * accent (design.md §2 — the accent is reserved for live signal, and a 50%
 * accent still reads as accent). Kept identical to the Convert CTA in the
 * paste pane this modal hosts, so one dialog never shows two disabled looks.
 */
function createButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? "var(--cta-disabled-bg)" : "var(--accent)",
    border: "none",
    borderRadius: "var(--radius-button)",
    padding: "var(--space-2) var(--space-4)",
    fontFamily: "var(--font-sans)",
    fontSize: "13px",
    color: disabled ? "var(--cta-disabled-fg)" : "var(--surface)",
    cursor: disabled ? "not-allowed" : "pointer",
    boxShadow: disabled ? "none" : "var(--shadow-cta)",
    minHeight: "var(--tap-target-min)",
    transition: "color var(--motion-state), background var(--motion-state)",
  };
}

const MAX_TITLE_LENGTH = 120;

// ─── Create sub-mode toggle (Blank ⇄ Paste a recipe) ──────────────────────────

type CreateSubMode = "blank" | "paste";

// The sub-mode toggle reuses the shared DiffSegmented control (features/diff) —
// same quiet active idiom (neutral surface fill + accent border on the active
// segment) and §4.4 inner radius as every other segmented control in the app.
const toggleRowStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  padding: "var(--space-3) var(--space-4) 0",
  flexShrink: 0,
};

// ─── Component ────────────────────────────────────────────────────────────────

export function RecipeCreationModal({
  mode,
  collections,
  onCreate,
  onClose,
  sourceRecipe,
  sourceRecipeCollectionUuids,
  existingRecipeTitles,
  headerLabel,
  submitLabel,
  submittingLabel,
  sourceContextLabel,
  pasteConvert,
  onPasteConverted,
  pasteClaims,
  probePasteClaims,
}: RecipeCreationModalProps) {
  const [title, setTitle] = useState("");
  // Paste-to-syntax (C-WP5). Only meaningful when `mode === "new"` and a
  // `pasteConvert` wire is supplied; otherwise the toggle is never rendered.
  const pasteCapable = mode === "new" && pasteConvert !== undefined;
  const [createSubMode, setCreateSubMode] = useState<CreateSubMode>("blank");
  // F8 — discard guard for the paste pane. The pane reports `busy` (a convert is
  // in flight) and `dirty` (the textarea is non-empty) here via onGuardChange.
  // A ref (not state) so the overlay-click / Escape handlers read the latest
  // values without re-subscribing, and so keystrokes don't re-render the modal.
  const pasteGuardRef = useRef<{ busy: boolean; dirty: boolean }>({ busy: false, dirty: false });
  // `spinoff` mode pre-populates from the source recipe's memberships; `new`
  // and `variation` start with no selection. The picker is hidden for
  // `variation` mode (variations inherit their parent's collections).
  const [selectedCollectionUuids, setSelectedCollectionUuids] = useState<Set<string>>(() =>
    mode === "spinoff" && sourceRecipeCollectionUuids
      ? new Set(sourceRecipeCollectionUuids)
      : new Set(),
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // 2026-05-04 — error-handling + observability split. The reporter classifies
  // mutate-handler failures into user (slug_taken / invalid_slug / quota /
  // unauthorised / forbidden / validation) vs system (network / timeout /
  // 5xx / unknown). Mode-specific copy comes from the classifier; system
  // failures additionally fire a best-effort observability event so the
  // operator can grep `wrangler tail proof-api-dev --search "client-error"`.
  // User-input strings (the recipe title) NEVER reach the reporter — only
  // structural IDs in the `context` arg.
  const reportError = useErrorReporter();

  // Whether the collection picker is shown. Variations inherit parent's
  // collections — no picker. New + spinoff both show it.
  const showCollections = mode !== "variation";

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") {
        // F8 — never discard mid-convert. Escape stays live otherwise (explicit
        // intent), even with pasted text (the documented fallback — the modal
        // family has no unsaved-changes confirm idiom to reuse; overlay-click is
        // the no-op that guards a stray backdrop tap).
        if (createSubMode === "paste" && pasteGuardRef.current.busy) return;
        onClose();
        return;
      }
      // Cmd/Ctrl+Enter submits the BLANK create only. In the paste sub-mode the
      // pane owns its own Convert action; a global shortcut here must not mint a
      // blank recipe behind the user's back.
      if (createSubMode === "paste") return;
      if ((e.key === "Enter" && (e.metaKey || e.ctrlKey)) || (e.key === "Return" && e.metaKey)) {
        e.preventDefault();
        void handleSubmit();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, title, selectedCollectionUuids, mode, createSubMode]);

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target !== overlayRef.current) return;
    // F8 — in the paste sub-mode, a backdrop tap must not discard an in-flight
    // convert or non-empty pasted text. Use the explicit Cancel / × instead.
    if (createSubMode === "paste" && (pasteGuardRef.current.busy || pasteGuardRef.current.dirty))
      return;
    onClose();
  }

  function handleTitleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setTitle(e.target.value.slice(0, MAX_TITLE_LENGTH));
    if (error) setError(null);
  }

  function handleTitleKeyDown(e: ReactKeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  }

  function toggleCollection(uuid: string) {
    setSelectedCollectionUuids((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }

  async function handleSubmit(e?: FormEvent) {
    e?.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      setError("Name is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Rule 8: time-bound the (opaque) onCreate promise. A stalled save that
      // never settles would otherwise leave "Saving…" latched with no error.
      await withTimeout(
        onCreate({
          title: trimmed,
          collectionUuids: Array.from(selectedCollectionUuids),
          mode,
        }),
        CREATE_TIMEOUT_MS,
        "recipe create",
      );
      onClose();
    } catch (err: unknown) {
      // Always log the structured event for the local console — useful in
      // dev + tests; the production observability path runs only for system
      // errors, via reportError() below.
      console.error({ event: "recipe_create_failed", mode, err });
      // A timed-out save is a hang, not a classifiable server response — point
      // the user at their connection and let them retry (the modal stays open).
      if (err instanceof TimeoutError) {
        setError("Could not save. Check your connection and try again.");
        return;
      }
      // Classify into user (slug_taken etc.) or system (5xx / network / unknown).
      // For system errors the helper fires a best-effort POST to
      // /rpc/clientError/report so the operator sees a [client-error] log line.
      // For user errors it does NOT report — the user pressing the wrong
      // button is not a server bug.
      //
      // Privacy: NO user-input strings (recipe title, body content) are passed
      // through `context` — only the mode key + the optional source-recipe
      // UUID for spin-off / variation.
      const reportContext: Parameters<typeof reportError>[3] = { mode };
      if (sourceRecipe?.uuid) reportContext["sourceRecipeUuid"] = sourceRecipe.uuid;
      const classified = reportError(
        "recipe_create_failed",
        err,
        { entity: "recipe", mode },
        reportContext,
      );
      setError(classified.copy);
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = title.trim().length > 0 && !submitting;

  // Duplicate-name hint (non-blocking). If the typed name case-insensitively
  // matches an existing recipe title, we surface an informative note — the
  // Create button stays enabled (duplicate titles are allowed; this is a heads-
  // up, not a gate). Only meaningful when the caller passes existingRecipeTitles.
  const trimmedTitleLower = title.trim().toLowerCase();
  const isDuplicateName =
    trimmedTitleLower.length > 0 &&
    (existingRecipeTitles ?? []).some((t) => t.trim().toLowerCase() === trimmedTitleLower);

  // ── Mode-keyed copy ─────────────────────────────────────────────────────────
  const headerCopy: Record<RecipeCreationMode, string> = {
    new: "New recipe",
    spinoff: "Spin-off recipe",
    variation: "New variation",
  };
  const submitCopy: Record<RecipeCreationMode, string> = {
    new: "Create",
    spinoff: "Spin it off",
    variation: "Create variation",
  };
  const savingCopy: Record<RecipeCreationMode, string> = {
    new: "Saving…",
    spinoff: "Spinning off…",
    variation: "Creating…",
  };
  const namePlaceholder: Record<RecipeCreationMode, string> = {
    new: "Pasta carbonara",
    spinoff: "New name for the spin-off",
    variation: "e.g. High hydration",
  };

  // Copy-override resolution (nav-chrome-contextual-create §4.3) — the Duplicate
  // entry point reuses `mode="spinoff"` but presents "Duplicate …" copy.
  const resolvedHeader = headerLabel ?? headerCopy[mode];
  const resolvedSubmit = submitLabel ?? submitCopy[mode];
  const resolvedSubmitting = submittingLabel ?? savingCopy[mode];
  const resolvedSourceContext =
    sourceContextLabel ?? (mode === "spinoff" ? "Spin-off from: " : "Variation of: ");

  return (
    <>
      <style>{SHEET_CSS}</style>
      <div
        ref={overlayRef}
        className="proof-recipe-create-overlay"
        style={overlayStyle}
        role="dialog"
        aria-modal="true"
        aria-label={resolvedHeader}
        onClick={handleOverlayClick}
        data-testid="recipe-creation-modal"
        data-mode={mode}
      >
        <div className="proof-recipe-create-panel" style={panelStyle}>
          <div style={headerStyle}>
            <span style={titleStyle}>{resolvedHeader}</span>
            <button type="button" style={closeButtonStyle} aria-label="Close" onClick={onClose}>
              ×
            </button>
          </div>

          {/* Paste-to-syntax toggle (C-WP5) — Blank ⇄ Paste a recipe. Only on the
              `new` mode with a paste wire; spin-off / variation never show it. */}
          {pasteCapable && (
            <div style={toggleRowStyle}>
              <DiffSegmented
                groupLabel="Create mode"
                testId="create-submode-toggle"
                value={createSubMode}
                onChange={setCreateSubMode}
                options={[
                  { value: "blank", label: "Blank", testId: "create-submode-blank" },
                  { value: "paste", label: "Paste a recipe", testId: "create-submode-paste" },
                ]}
              />
            </div>
          )}

          {pasteCapable && createSubMode === "paste" ? (
            <PasteConvertPane
              title={title}
              onTitleChange={(v) => {
                setTitle(v.slice(0, MAX_TITLE_LENGTH));
                if (error) setError(null);
              }}
              convert={pasteConvert!}
              onConverted={(recipeUuid, title) => onPasteConverted?.(recipeUuid, title)}
              onCancel={onClose}
              onGuardChange={(busy, dirty) => {
                pasteGuardRef.current = { busy, dirty };
              }}
              {...(pasteClaims !== undefined ? { claims: pasteClaims } : {})}
              {...(probePasteClaims !== undefined ? { probeClaims: probePasteClaims } : {})}
              inputStyle={inputStyle}
              labelStyle={labelStyle}
              maxTitleLength={MAX_TITLE_LENGTH}
            />
          ) : (
            <form onSubmit={(e) => void handleSubmit(e)} style={{ display: "contents" }}>
              <div style={bodyStyle}>
                {/* Source context line — shown for spinoff + variation modes. */}
                {mode !== "new" && sourceRecipe !== undefined && (
                  <div style={labelStyle} data-testid="recipe-create-source-context">
                    {resolvedSourceContext}
                    <span style={{ color: "var(--text)" }}>{sourceRecipe.title}</span>
                  </div>
                )}

                {/* Name input — shared across all three modes. Empty default in
                  every mode; the user MUST type a fresh name. */}
                <div>
                  <label htmlFor="proof-recipe-create-title" style={labelStyle}>
                    Name
                  </label>
                  <input
                    ref={inputRef}
                    id="proof-recipe-create-title"
                    type="text"
                    value={title}
                    onChange={handleTitleChange}
                    onKeyDown={handleTitleKeyDown}
                    style={inputStyle(error !== null)}
                    maxLength={MAX_TITLE_LENGTH}
                    autoComplete="off"
                    placeholder={namePlaceholder[mode]}
                    disabled={submitting}
                    data-testid="recipe-create-title"
                  />
                  {/* Duplicate-name hint — informative, not a gate. The Create
                    button stays enabled; duplicate titles are allowed. */}
                  {isDuplicateName && (
                    <span
                      role="status"
                      style={duplicateHintStyle}
                      data-testid="recipe-create-duplicate-hint"
                    >
                      You already have a recipe with this name.
                    </span>
                  )}
                </div>

                {/* Collections picker — `new` and `spinoff` only.
                  Variations inherit their parent's collections, so the picker
                  is hidden for `variation` mode (locked design 2026-05-03). */}
                {showCollections && (
                  <div>
                    <span style={labelStyle}>Collections</span>
                    <div style={{ height: "var(--space-1)" }} />
                    {collections.length === 0 ? (
                      <div style={emptyCollectionsStyle} data-testid="recipe-create-no-collections">
                        No collections yet.
                      </div>
                    ) : (
                      <div style={collectionListStyle} role="list">
                        {collections.map((col) => {
                          const checked = selectedCollectionUuids.has(col.uuid);
                          return (
                            <label
                              key={col.uuid}
                              className="proof-recipe-create-collection-row"
                              role="listitem"
                              data-testid={`recipe-create-collection-${col.uuid}`}
                            >
                              <input
                                type="checkbox"
                                style={checkboxStyle}
                                checked={checked}
                                onChange={() => toggleCollection(col.uuid)}
                                aria-label={col.name}
                              />
                              <span style={collectionLabelStyle}>{col.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {error !== null && (
                  <span role="alert" style={errorStyle} data-testid="recipe-create-error">
                    {error}
                  </span>
                )}
              </div>

              <div style={footerStyle}>
                <button
                  type="button"
                  style={cancelButtonStyle()}
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={createButtonStyle(!canSubmit)}
                  disabled={!canSubmit}
                  data-testid="recipe-create-submit"
                >
                  {submitting ? resolvedSubmitting : resolvedSubmit}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
